import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { IconButton } from '@/components/atoms/IconButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/molecules/Card';
import { useResources, useStory } from '@/hooks/useBackofficeData';
import { buildRoute, ROUTES } from '@/constants/routes';
import { ResourceForm, type ResourceFormData, type ResourceFormRef } from '@/components/molecules/ResourceForm';
import styles from './StoryResourceFormPage.module.scss';

export function StoryResourceFormPage() {
  const navigate = useNavigate();
  const { storyId: storyId, resourceId } = useParams<{ storyId: string; resourceId: string }>();
  const resourceFormRef = useRef<ResourceFormRef>(null);
  const [sidebarContainer, setSidebarContainer] = useState<HTMLDivElement | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const isEditing = Boolean(resourceId);

  const { story } = useStory(storyId);
  const { resources, isLoading, saveResource } = useResources(storyId);

  const existingResource = isEditing ? resources.find((r) => r.id === resourceId) : undefined;
  const storyType = story?.type ?? 'general';

  const handleBack = () => {
    if (storyId) {
      const url = buildRoute(ROUTES.BACKOFFICE.STORY_EDIT, { id: storyId });
      navigate(`${url}?tab=resources`);
    } else {
      navigate(-1);
    }
  };

  const handleSave = async (formData: ResourceFormData) => {
    await saveResource(formData);
    handleBack();
  };

  if (isLoading) {
    return (
      <div className={styles.storyResourceForm}>
        <div className={styles.storyResourceForm__loading}>Cargando...</div>
      </div>
    );
  }

  if (isEditing && !existingResource) {
    return (
      <div className={styles.storyResourceForm}>
        <div className={styles.storyResourceForm__loading}>Recurso no encontrado.</div>
      </div>
    );
  }

  return (
    <div className={styles.storyResourceForm}>
      <div className={styles.storyResourceForm__toolbar}>
        <div className={styles.storyResourceForm__header}>
          <IconButton icon={ArrowLeft} onClick={handleBack} aria-label="Volver a recursos de la historia" />
          <h1 className={styles.storyResourceForm__title}>
            {isEditing ? 'Editar recurso' : 'Añadir recurso'}
          </h1>
          <div className={styles.storyResourceForm__headerActions}>
            <IconButton
              icon={Save}
              variant="primary"
              onClick={() => resourceFormRef.current?.submit()}
              aria-label="Guardar"
            />
            <IconButton
              icon={isSidebarOpen ? PanelRightClose : PanelRightOpen}
              onClick={() => setIsSidebarOpen((v) => !v)}
              aria-label={isSidebarOpen ? 'Ocultar panel de información' : 'Mostrar panel de información'}
            />
          </div>
        </div>
      </div>

      <div className={styles.storyResourceForm__body} data-sidebar-open={isSidebarOpen}>
        <div className={styles.storyResourceForm__main}>
          <ResourceForm
            ref={resourceFormRef}
            mode={isEditing ? 'edit' : 'create'}
            resource={existingResource}
            storyType={storyType}
            nextOrder={resources.length + 1}
            onSave={handleSave}
            onCancel={handleBack}
            layout="editor"
            sidebarPortalTarget={sidebarContainer}
          />
        </div>
        {isSidebarOpen && (
        <aside className={styles.storyResourceForm__sidebar}>
          <Card className={styles.storyResourceForm__sidebarCard}>
            <CardHeader>
              <CardTitle>Información</CardTitle>
            </CardHeader>
            <CardContent className={styles.storyResourceForm__sidebarContent}>
              <div ref={(el) => setSidebarContainer(el)} />
            </CardContent>
          </Card>
        </aside>
        )}
      </div>
    </div>
  );
}

export default StoryResourceFormPage;
